import React, {useCallback, useEffect, useRef} from 'react';

import {Modal} from '../../modal';
import {cls} from '../../utils';
import {CrossIcon} from '../svg-icon';

import css from './modal-popup.module.css';

type Props = {
	isModal?: boolean;
	onClose?: () => void;
	open: boolean;
	buttons?: any;
	className?: string;
	theme?: string;
	maxWidth?: number;
	header?: string;
	children?: React.ReactNode;
	/**
	 * Своя запись в истории, чтобы «назад» закрывало окно. Окну, которое открыто адресом,
	 * она не нужна и мешает: «назад» возвращает на адрес окна, оно открывается снова и снова
	 * кладёт запись — из него не выйти.
	 */
	historyEntry?: boolean;
};

let openPopups = 0;
/** номера открытых окон по порядку открытия: по ним окно узнаёт, что оно самое верхнее */
const openStack: number[] = [];
let lastPopupKey = 0;

export function toggleHtmlClass(open: boolean) {
	const documentElement = document.documentElement;
	if (!documentElement) return;
	// окон может быть несколько: страница возвращает скролл, когда закрылось последнее
	openPopups = Math.max(0, openPopups + (open ? 1 : -1));
	documentElement.classList.toggle('is-modal-popup-open', openPopups > 0);
}

export const ModalPopup: React.FC<Props> = (props) => {
	const {isModal, onClose, open, children, header, className, theme = 'info', maxWidth, historyEntry = true, ...other} = props;
	const wrapper = useRef<HTMLDivElement | null>(null);
	const headerId = React.useId();

	const setFocus = useCallback(() => {
		if (wrapper && wrapper.current) {
			wrapper.current.focus();
		}
	}, []);

	const close = useCallback(() => {
		if (!isModal && onClose) onClose();
	}, [isModal, onClose]);

	useEffect(() => {
		if (!open) return;
		toggleHtmlClass(true);

		return () => toggleHtmlClass(false);
	}, [open]);

	const popupKey = useRef(0);

	useEffect(() => {
		if (!open) return;
		popupKey.current = ++lastPopupKey;
		openStack.push(popupKey.current);

		return () => {
			const index = openStack.indexOf(popupKey.current);
			if (index >= 0) openStack.splice(index, 1);
		};
	}, [open]);

	useEffect(() => {
		function checkEscapeKey(e: KeyboardEvent) {
			// закрывается только верхнее окно: иначе Escape во вложенном окне закрывает и то, из которого его открыли
			if (e.keyCode === 27 && open && openStack[openStack.length - 1] === popupKey.current) close();
		}

		document.addEventListener('keyup', checkEscapeKey);

		return () => {
			document.removeEventListener('keyup', checkEscapeKey);
		};
	}, [open, close]);

	/** обработчик закрытия меняется при каждой отрисовке родителя: запись истории от этого не пересоздаётся */
	const onCloseRef = useRef(onClose);
	onCloseRef.current = onClose;
	const canClose = !!onClose;

	useEffect(() => {
		if (!open || isModal || !canClose || !historyEntry) return;

		// своя запись в истории: кнопка «назад» закрывает окно. У вложенных окон записи свои, поэтому
		// у записи есть номер — закрываясь, окно снимает только свою, и соседние от этого не закрываются
		const key = popupKey.current;
		history.pushState({modalOpen: true, popupKey: key}, '');

		const handlePopState = (event: PopStateEvent) => {
			if ((event.state?.popupKey ?? 0) < key) onCloseRef.current?.();
		};
		window.addEventListener('popstate', handlePopState);

		return () => {
			window.removeEventListener('popstate', handlePopState);
			if (history.state?.popupKey === key) {
				history.back();
			}
		};
	}, [open, isModal, canClose, historyEntry]);

	if (!open) return null;

	return (
		<Modal>
			<div className={cls(css.wrapper, className)} ref={wrapper} tabIndex={0}>
				<div className={cls(css.backdrop, 'modal-popup-backdrop in')} onClick={close} />
				<div className={css.container}>
					<div
						className={cls('modal-popup', css.popup)}
						style={maxWidth ? {maxWidth} : undefined}
						role="dialog"
						aria-modal="true"
						aria-labelledby={header ? headerId : undefined}
					>
						<div className={css.popupMain} {...other} onClick={(event) => event.stopPropagation()}>
							{!isModal && (
								<button
									type="button"
									className={cls(css.close, 'modal-popup-close')}
									onClick={close}
									aria-label={'Закрыть'}
								>
									<CrossIcon width={24} height={24} />
								</button>
							)}

							<h1 className={css.header} id={headerId}>
								{header}
							</h1>
							{children && (
								<section className={cls(css.content, 'modal-popup-content')}>{children}</section>
							)}
						</div>
					</div>
				</div>
			</div>
			{/*Что бы отловить уход из popup*/}
			<div tabIndex={0} onBlur={setFocus} />
			{/*Что бы фокус не пригнул в адресную строку (в этом случае его уже не поменять в chrome)*/}
			<div tabIndex={0} />
		</Modal>
	);
};

